import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Trial / renewal banner shown at the top of every dashboard page.
 * - Solo trial: appears from day 20 (10 days remaining) onwards.
 * - Studio monthly/annual: appears 10 days before subscription_expires_at.
 * Dismissible per-session (localStorage) but re-appears the next day.
 */
export function PlanBanner() {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/plan-status");
        if (!mounted) return;
        setState(data);
      } catch {
        /* silent — not fatal */
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!state?.banner) return;
    try {
      const key = `kairosa_plan_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;
      setDismissed(localStorage.getItem(key) === "1");
    } catch {}
  }, [state]);

  if (!state?.banner || dismissed) return null;
  const b = state.banner;

  const tone =
    b.kind === "solo-trial-expired" || b.kind === "studio-renewal-due" && (b.days_remaining ?? 99) <= 3
      ? "border-destructive/50 bg-destructive/10 text-destructive"
      : "border-primary/40 bg-primary/10 text-foreground";

  return (
    <div data-testid="plan-banner" className={"relative mx-auto max-w-7xl mt-4 mx-4 md:mx-6 rounded-xl border px-4 py-3 flex items-center gap-3 " + tone}>
      <AlertCircle className="h-5 w-5 shrink-0" />
      <div className="flex-1 text-sm">
        <span className="font-medium">{b.message}</span>
      </div>
      <Link to={b.cta_href || "/upgrade"}>
        <button
          data-testid="plan-banner-cta"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] rounded-full border border-current px-3 py-1.5 hover:bg-foreground/5 transition-colors"
        >
          {b.cta_label} <ArrowRight className="h-3 w-3" />
        </button>
      </Link>
      <button
        data-testid="plan-banner-dismiss"
        onClick={() => {
          const key = `kairosa_plan_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;
          try { localStorage.setItem(key, "1"); } catch {}
          setDismissed(true);
        }}
        className="p-1 rounded hover:bg-foreground/10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default PlanBanner;
